import React, { useState } from 'react'
import { Modal, Carousel } from 'react-bootstrap'
import { FaImage, FaFilePdf, FaFileAlt, FaLink, FaDownload } from 'react-icons/fa'

import { downloadImage } from '../../../utils/download'

// Shared insight helpers + presentational components used by both the ADAS
// section (AdasSections.js) and the read-only Mechanical section
// (MechanicalSection.js). Extracted verbatim from AdasSections.js so the two
// sections render insights identically; AdasSections behavior is unchanged.

export const ADAS_SLOT_OEM_POSITION = 'oem-position'
export const ADAS_SLOT_OEM_PROCEDURE = 'oem-procedure'
export const ADAS_SLOT_ADDITIONAL_MEDIA = 'additional-media'

// The ServiceModule stores this sensor as the singular "Diagnostic Scan", but
// the approved designs show it pluralized ("Diagnostic Scans") in both the
// section header and the "… Insights" toggle. The DB value is shared widely and
// must not change, so pluralize at render time only.
const SENSOR_DISPLAY_NAMES = {
  'Diagnostic Scan': 'Diagnostic Scans',
}
export const displaySensorName = (name) => (name && SENSOR_DISPLAY_NAMES[name]) || name

export const formatPrice = (amount) => {
  return `$ ${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export const getDomainTag = (file, slot) => {
  const raw = file && file.fileURL
  if (raw) {
    // Prepend a scheme so schemeless links ("oem1stop.com") still parse — a bare
    // URL would otherwise throw in new URL() and lose its tag (Finding 26).
    const normalized = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : 'https://' + raw
    try {
      const hostname = new URL(normalized).hostname
      if (hostname.includes('oem1stop.com')) return 'OEM1Stop'
      if (hostname.includes('alldata.com')) return 'ALLDATA'
      if (hostname.includes('repairlogic.com')) return 'RepairLogic'
    } catch (e) {
      // invalid URL
    }
  }
  // Generic OEM tag for OEM-documentation slots — adasThink-generated OR manually
  // added — so every OEM link is labeled even when the domain isn't recognized.
  const isOemSlot = slot === ADAS_SLOT_OEM_POSITION || slot === ADAS_SLOT_OEM_PROCEDURE
  if (isOemSlot || (file && file.source === 'adasThink')) return 'OEM'
  return null
}

export const getSensorInsight = (services, sensorInsights) => {
  for (const svc of services) {
    if (svc.rawSensorId) {
      const match = (sensorInsights || []).find(si => si.sensorId === svc.rawSensorId)
      if (match) return match
    }
  }
  return null
}

export const getSensorDocuments = (report, services) => {
  const documents = report.documents || []
  const sensorSlugs = services.map(svc => svc.sensorId).filter(Boolean)
  return documents.filter((doc) => {
    if (doc.service !== 'adas') return false
    if (doc.sensorSlug) return sensorSlugs.includes(doc.sensorSlug)
    return sensorSlugs.includes(doc.sensorId)
  })
}

export const getSensorPhotos = (report, services) => {
  const photos = report.photos || []
  const sensorSlugs = services.map(svc => svc.sensorId).filter(Boolean)
  return photos.filter(photo => (
    photo.service === 'adas' &&
    photo.slot !== 'adas-document' &&
    sensorSlugs.includes(photo.sensorId)
  ))
}

export const getLinkedSafetySystems = (report, services) => {
  const safetyFeatures = report.safetyFeatures || []
  const sensorKeys = {}

  // feature.linkedSensorIds are raw adasThink UUIDs — match on rawSensorId
  // only. Two sensors can collapse into one subcategory, so matching the slug
  // sensorId would cross-link equipped systems into the wrong subcategory.
  services.forEach((svc) => {
    if (svc.rawSensorId) sensorKeys[String(svc.rawSensorId)] = true
  })

  const systems = []
  const seen = {}
  safetyFeatures.forEach((feature) => {
    if (!feature || feature.answer !== 'equipped') return
    const linked = feature.linkedSensorIds || []
    if (!linked.length) return
    const isLinked = linked.some(id => sensorKeys[String(id)])
    if (isLinked && feature.name && !seen[feature.name]) {
      seen[feature.name] = true
      systems.push({ name: feature.name, oemName: feature.oemName })
    }
  })

  return systems
}

const getConditionVariant = (status) => {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'repair') return 'repair'
  if (normalized === 'replace') return 'replace'
  return 'install'
}

const getConditionLabel = (status) => {
  if (status === 'Install') return 'Installed'
  if (status === 'Repair') return 'Repaired'
  if (status === 'Replace') return 'Replaced'
  return status || 'Unknown'
}

// Classify an attachment by extension so we can show the right icon (F25) and
// decide open behavior. Mirrors the admin portal's getAttachmentType.
const getRowType = (url) => {
  const clean = String(url || '').split('?')[0].toLowerCase()
  if (/\.(png|jpe?g|gif|webp|bmp|heic|svg)$/.test(clean)) return 'image'
  if (/\.pdf$/.test(clean)) return 'pdf'
  if (/\.(docx?|xlsx?|pptx?|txt|csv)$/.test(clean)) return 'file'
  if (/^https?:\/\//.test(String(url || ''))) return 'link'
  return 'file'
}

const RowIcon = ({ url }) => {
  const type = getRowType(url)
  if (type === 'image') return <FaImage className='adas-detail__doc-file-icon' />
  if (type === 'pdf') return <FaFilePdf className='adas-detail__doc-file-icon' />
  if (type === 'link') return <FaLink className='adas-detail__doc-file-icon' />
  return <FaFileAlt className='adas-detail__doc-file-icon' />
}

// When `onRowClick` is provided (the Photos tile), rows open an in-page photo
// viewer (F24); otherwise rows are links that open the file in a new tab.
// Every row shows a type icon (F25).
export const DocumentationTile = ({ title, rows, emptyMessage, showDomainTag = true, onRowClick = null }) => {
  return (
    <div className='adas-detail__doc-tile'>
      <div className='adas-detail__doc-tile-header'>
        <span className='adas-detail__doc-tile-title'>{title}</span>
      </div>
      <div className='adas-detail__doc-tile-files'>
        {rows.length ? rows.map((row, idx) => (
          <div key={`${title}-${idx}`} className='adas-detail__doc-file-row'>
            {onRowClick ? (
              <button
                type='button'
                className='adas-detail__doc-file-link adas-detail__doc-file-btn'
                onClick={() => onRowClick(idx)}
              >
                <RowIcon url={row.url} />
                <span className='adas-detail__doc-file-name'>{row.name}</span>
              </button>
            ) : (
              <a
                href={row.url}
                target='_blank'
                rel='noopener noreferrer'
                className='adas-detail__doc-file-link'
              >
                <RowIcon url={row.url} />
                <span className='adas-detail__doc-file-name'>{row.name}</span>
              </a>
            )}
            {showDomainTag && row.tag && (
              <span className='adas-detail__doc-domain-tag'>{row.tag}</span>
            )}
          </div>
        )) : (
          <span className='adas-detail__doc-tile-empty'>{emptyMessage}</span>
        )}
      </div>
    </div>
  )
}

export const StatusBadge = ({ status, source }) => {
  if (!status) return null

  if (source === 'manual' && status !== 'removed') {
    return (
      <span className='adas-detail__status-badge custom'>
        Custom
      </span>
    )
  }

  let label = 'Potential'
  let variant = 'potential'

  if (status === 'required') {
    label = 'Required'
    variant = 'required'
  } else if (status === 'removed') {
    label = 'Removed'
    variant = 'removed'
  }

  return (
    <span className={`adas-detail__status-badge ${variant}`}>
      {label}
    </span>
  )
}

const SensorInsights = ({ sensorName, services, sensorInsights, report, collision, forceOpen, includeCustom }) => {
  const [open, setOpen] = useState(false)
  const [lightbox, setLightbox] = useState({ open: false, index: 0 })
  const insight = getSensorInsight(services, sensorInsights)
  const hasInsights = !!insight
  const isManualGroup = services.some(svc => svc.recommendedSource === 'manual')
  // AdasSections never passes includeCustom, so showInsights reduces to the
  // original (hasInsights || isManualGroup) there. The Mechanical section opts
  // in via includeCustom so a custom-only group still renders the (blank)
  // Additional Documentation block.
  const isCustomGroup = includeCustom && services.some(svc => svc.custom)
  const showInsights = hasInsights || isManualGroup || isCustomGroup

  if (!showInsights) return null

  const isOpen = forceOpen || open
  const make = ((report.vehicle && report.vehicle.make) || '').toLowerCase()
  const collisionPhrase = make === 'ford' ? 'an accident' : 'a collision'
  // Admin decides the collision justification per sensor group (services.some(collisionRelated)),
  // and hides collision services entirely when the vehicle wasn't in a collision. Mirror that net
  // outcome: only show the collision text when the report is a collision AND this group actually
  // contains a collision-triggered service — otherwise fall back to the repair-manual conditions.
  const isCollisionGroup = !!collision && services.some(svc => svc.collisionRelated)

  const sensorDocs = getSensorDocuments(report, services)
  const matchSlot = slot => sensorDocs.filter(doc => doc.slot === slot)
  const positionDocs = matchSlot(ADAS_SLOT_OEM_POSITION)
  const procedureDocs = matchSlot(ADAS_SLOT_OEM_PROCEDURE)
  // Additional Media catches its own slot PLUS any slot-less / unknown-slot docs
  // (e.g. links/files added from the mobile app, which carry no slot) so they are
  // never silently dropped — parity with the admin portal (QA #23).
  const mediaDocs = sensorDocs.filter(doc => (
    doc.slot === ADAS_SLOT_ADDITIONAL_MEDIA ||
    (doc.slot !== ADAS_SLOT_OEM_POSITION && doc.slot !== ADAS_SLOT_OEM_PROCEDURE)
  ))
  const sensorPhotos = getSensorPhotos(report, services)

  const mapDocRows = docs => docs.flatMap((doc) => (
    (doc.files || []).map((file) => ({
      url: file.fileURL,
      name: file.note || (file.fileURL ? file.fileURL.split('/').pop() : '') || doc.name,
      tag: getDomainTag(file, doc.slot),
    }))
  )).filter(row => !!row.url)

  const positionRows = mapDocRows(positionDocs)
  const procedureRows = mapDocRows(procedureDocs)
  const mediaRows = mapDocRows(mediaDocs)
  const photoRows = sensorPhotos
    .map(photo => ({
      url: photo.url,
      // downloadUrl is attachment-signed by the portal API so the lightbox forces
      // a real file download instead of opening the image in a tab (QA #31).
      downloadUrl: photo.downloadUrl || photo.url,
      name: photo.name || photo.note || (photo.url ? photo.url.split('/').pop() : '') || 'Photo',
    }))
    .filter(row => !!row.url)

  const linkedSafetySystems = getLinkedSafetySystems(report, services)

  return (
    <div className='adas-detail__insights'>
      <button className='adas-detail__insights-toggle' onClick={() => setOpen(!open)}>
        <span>{displaySensorName(sensorName || 'Sensor')} Insights</span>
        <span className={`adas-detail__chevron ${isOpen ? 'adas-detail__chevron--expanded' : ''}`}>
          &#9662;
        </span>
      </button>
      {isOpen && (
        <div className='adas-detail__insights-content'>
          {hasInsights && (
            <div className='adas-detail__sensor-insights'>
              <h4 className='adas-detail__sensor-insights-title'>
                Why These Services are Required
              </h4>
              {isCollisionGroup ? (
                <p className='adas-detail__insight-justification'>
                  Service(s) are required due to the vehicle being in {collisionPhrase}.
                </p>
              ) : (
                <>
                  <p className='adas-detail__insight-justification'>
                    Per the {report.vehicle && report.vehicle.year} {report.vehicle && report.vehicle.make}{' '}
                    {report.vehicle && report.vehicle.model} {report.vehicle && report.vehicle.trim ? report.vehicle.trim : ''} repair
                    manual, the {sensorName} requires the above mentioned procedure(s) should any of the following occur:
                  </p>
                  <div className='adas-detail__conditions-grid'>
                    {(insight.conditions || []).map((condition, idx) => {
                      const conditionItem = condition.items && condition.items[0]
                      const status = conditionItem && conditionItem.status
                      const lineRefs = conditionItem && conditionItem.lineReferences
                      const variant = getConditionVariant(status)
                      const label = getConditionLabel(status)

                      return (
                        <div key={idx} className='adas-detail__condition-tile'>
                          <span className='adas-detail__condition-component'>
                            {condition.component}
                          </span>
                          <div className={`adas-detail__condition-tile-bottom adas-detail__condition-tile-bottom--${variant}`}>
                            <span className='adas-detail__condition-status'>
                              {label}
                            </span>
                            {lineRefs && (
                              <span className='adas-detail__condition-lines'>
                                Lines {lineRefs}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <div className='adas-detail__additional-docs'>
            <h4 className='adas-detail__additional-docs-title'>Additional Documentation</h4>
            <div className='adas-detail__doc-tiles-grid'>
              <DocumentationTile
                title='OEM Position Statement'
                rows={positionRows}
                emptyMessage='No documents to show'
              />
              <DocumentationTile
                title='OEM Procedure'
                rows={procedureRows}
                emptyMessage='No documents to show'
              />
              <DocumentationTile
                title={`${displaySensorName(sensorName)} Photos`}
                rows={photoRows}
                emptyMessage='No photos to show'
                showDomainTag={false}
                onRowClick={(idx) => setLightbox({ open: true, index: idx })}
              />
              <DocumentationTile
                title='Additional Media'
                rows={mediaRows}
                emptyMessage='No media to show'
                showDomainTag={false}
              />
            </div>
          </div>

          {linkedSafetySystems.length > 0 && (
            <div className='adas-detail__safety-systems'>
              <h4 className='adas-detail__safety-systems-title'>
                Equipped Safety Systems
              </h4>
              <p className='adas-detail__safety-systems-subtitle'>
                The {sensorName} is responsible for the following safety systems:
              </p>
              <div className='adas-detail__safety-systems-grid'>
                {linkedSafetySystems.map(system => (
                  <div key={system.name} className='adas-detail__safety-system-card'>
                    <span className='adas-detail__safety-system-name'>
                      {system.name}
                    </span>
                    {system.oemName && system.oemName !== system.name && (
                      <span className='adas-detail__safety-system-detail'>
                        ({system.oemName})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {lightbox.open && photoRows.length > 0 && (
        <Modal
          show
          onHide={() => setLightbox({ open: false, index: 0 })}
          centered
          size='lg'
          dialogClassName='adas-photo-lightbox'
        >
          <button
            className='adas-modal-close'
            onClick={() => setLightbox({ open: false, index: 0 })}
            aria-label='Close'
          >
            &times;
          </button>
          <Modal.Body className='adas-photo-lightbox__body'>
            <Carousel
              activeIndex={lightbox.index}
              onSelect={(i) => setLightbox(prev => ({ ...prev, index: i }))}
              interval={null}
              indicators={photoRows.length > 1}
              controls={photoRows.length > 1}
            >
              {photoRows.map((row, idx) => (
                <Carousel.Item key={`lb-${idx}`}>
                  <img src={row.url} alt={row.name || 'Photo'} className='adas-photo-lightbox__img' />
                  {row.name && (
                    <p className='adas-photo-lightbox__caption'>{row.name}</p>
                  )}
                </Carousel.Item>
              ))}
            </Carousel>
            <div className='adas-photo-lightbox__actions'>
              <button
                type='button'
                className='adas-photo-lightbox__download'
                onClick={() => {
                  // Download the item currently shown in the carousel, matching
                  // the admin portal's photo viewer (Finding 30).
                  const active = photoRows[lightbox.index] || photoRows[0]
                  if (active) downloadImage(active.downloadUrl || active.url, active.name || 'photo')
                }}
              >
                Download Photo
                <FaDownload className='adas-photo-lightbox__download-icon' />
              </button>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  )
}

export default SensorInsights
